import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import { fabric } from "fabric";
import { pipe, equals, filter, pluck, innerJoin, includes, propEq } from "ramda";
import generateUUID from "uuid/v4";
import EventBus from "~/libs/eventBus";
import { styled } from "@mui/material/styles";

import InlineMenu from "~/components/InlineMenu";

import { createShape } from "~/libs/fabric";
import { getDeviceDependentDpr, getScreenWidth } from "~/utils";
import {
  FILLED_RECTANGLE,
  DASHED_RECTANGLE,
  FILLED_CIRCLE,
  DASHED_CIRCLE,
  ARROW,
  TEXT,
  FREE_DRAWING,
  MARKER,
  TRIANGLE,
  FILLED_TRIANGLE,
  LINE
} from "~/constants/shape-types";
import { DEFAULT_STROKE_WIDTH } from "~/store/ducks/application/fabric";
import { ARROW_DEFAULT_DIMENSIONS } from "~/libs/fabric/shapes";

import IconButton from "@mui/material/IconButton";
import BoldIcon from "@mui/icons-material/FormatBold";
import ItalicIcon from "@mui/icons-material/FormatItalic";
import UnderlinedIcon from "@mui/icons-material/FormatUnderlined";

import FreeDrawingIcon from "~/icons/create.svg";
import RectangleFilledIcon from "~/icons/crop-din-filled.svg";
import RectangleIcon from "~/icons/crop-din.svg";
import EllipseIconFilled from "~/icons/lens.svg";
import EllipseIcon from "~/icons/panorama-fish-eye.svg";
import ArrowIcon from "~/icons/arrow-left-alt.svg";
import TextIcon from "~/icons/text.svg";
import MarkerIcon from "~/icons/marker_noline.svg";
import LineIcon from "~/icons/line.svg";

const getBoldSelection = pipe(pluck("fontWeight"), filter(equals("bold")));

const getItalicSelection = pipe(pluck("fontStyle"), filter(equals("italic")));

const getUnderlineSelection = pipe(pluck("underline"), filter(equals(true)));

const oneTimeShapes = [FILLED_RECTANGLE, DASHED_RECTANGLE, FILLED_CIRCLE, DASHED_CIRCLE, TEXT];

const multiClickShapes = [TRIANGLE, FILLED_TRIANGLE, ARROW, LINE];

const styles = {
  cloth: {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    width: "100%",
    height: "100%"
  },
  canvas: {},
  menu: {
    position: "absolute",
    zIndex: 1305
  }
};

const Cloth = styled("div")(styles.cloth);

const Canvas = styled("canvas")(styles.canvas);

const Menu = styled("div")(styles.menu);

class LinenCloth extends Component {
  state = {
    isMouseDown: false,
    isShiftPressed: false,
    shape: null,
    temporaryShape: null
  };

  wrapperRef = createRef();

  canvasRef = createRef();

  intervalReferences = [];

  constructor(props) {
    super(props);

    this.onClearSelection = this.onClearSelection.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onPathCreated = this.onPathCreated.bind(this);
    this.onObjectMoved = this.onObjectMoved.bind(this);
    this.onObjectRotated = this.onObjectRotated.bind(this);
    this.onObjectModified = this.onObjectModified.bind(this);
    this.onSelectionCleared = this.onSelectionCleared.bind(this);
    this.onSelectionUpdated = this.onSelectionUpdated.bind(this);
    this.onSelectionCreated = this.onSelectionCreated.bind(this);
    this.triggerEvent = this.triggerEvent.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onClickBold = this.onClickBold.bind(this);
    this.onClickItalic = this.onClickItalic.bind(this);
    this.onClickUnderline = this.onClickUnderline.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onIssuedPrint = this.onIssuedPrint.bind(this);
    this.onFinishedIssuingPrint = this.onFinishedIssuingPrint.bind(this);
    this.clearSelectionForAll();
  }

  componentDidMount() {
    Object.entries(this.props.canvasSettings).forEach(([name, value]) => {
      fabric.Canvas.prototype[name] = value;
    });

    Object.entries(this.props.objectSettings).forEach(([name, value]) => {
      fabric.Object.prototype[name] = value;
    });
    const devicePixelRatio = getDeviceDependentDpr();
    let { cornerSize } = this.props.objectSettings;

    fabric.Object.prototype.controls.mtr.offsetY = this.props.rotatingPointOffset; // directly setting is deprecated

    // make cornerSize bigger in mobile devices, since it is too small to actually use
    if (getScreenWidth() < 1280) {
      cornerSize = 40;
      fabric.Object.prototype.cornerSize = cornerSize;
      fabric.Object.prototype.padding = 15;
      fabric.Object.prototype.controls.mtr.offsetY = -90;
    }
    this.setState({
      cornerSize
    });

    // before initialization (important)
    fabric.devicePixelRatio = devicePixelRatio;

    this.canvas = new fabric.Canvas(this.canvasRef.current, {
      isDrawingMode: this.props.isDrawingMode,
      width: this.props.pageWidth,
      height: this.props.pageHeight,
      enableRetinaScaling: true
    });
    // this.canvas.enablePointerEvents = true;

    this.canvas.getRetinaScaling = () => devicePixelRatio;

    this.canvas.on({
      "mouse:down": this.onMouseDown,
      "mouse:move": this.onMouseMove,
      "mouse:up": this.onMouseUp,
      "object:modified": this.onObjectModified,
      "path:created": this.onPathCreated,
      "selection:cleared": this.onSelectionCleared,
      "selection:updated": this.onSelectionUpdated,
      "selection:created": this.onSelectionCreated
    });

    EventBus.addListener("fabricClearSelection", this.onClearSelection);
    EventBus.addListener("issuedPrint", this.onIssuedPrint);
    EventBus.addListener("finishedIssuingPrint", this.onFinishedIssuingPrint);

    document.addEventListener("keydown", this.onKeyDown, false);
    document.addEventListener("keyup", this.onKeyUp, false);
    document.addEventListener("pointerup", this.onPointerUp);

    this.loadFromJSON();
  }

  // increase devicePixelRatio so print is not blurry
  onIssuedPrint() {
    const dpr = 4;
    fabric.devicePixelRatio = dpr;
    this.canvas._initRetinaScaling();
    this.canvas.getRetinaScaling = () => dpr;

    // re-render canvas with new quality
    this.canvas.requestRenderAll();
  }

  onFinishedIssuingPrint() {
    // reset devicePixelRatio to how it was before
    const dpr = getDeviceDependentDpr();
    fabric.devicePixelRatio = dpr;
    this.canvas._initRetinaScaling();
    this.canvas.getRetinaScaling = () => dpr;

    // re-render canvas with new quality
    this.canvas.requestRenderAll();
  }

  onClearSelection() {
    this.canvas.discardActiveObject();
    this.canvas.renderAll();
  }

  onPointerUp(event) {
    const { x, y } = event;
    const rect = this.canvas?.getElement()?.getBoundingClientRect();
    if (!rect) return;
    if (rect.left > x || x > rect.right || rect.top > y || y > rect.bottom) {
      // Point is outside canvas / bounding box
      const { temporaryShape } = this.state;
      /*
      if tempShape exists (arrow, line, polygon...) -> we remove
      so no invalid state can happen (of tempShape active in canvas,
      but unexisting in state)
      -> happens if we click the other canvas with active tempShape)
      No need to delete shapetype: new shape can be set, maybe on other canvas
      depends on where was clicked
      */
      if (temporaryShape) {
        this.canvas.remove(temporaryShape);
        this.setState({
          temporaryShape: null
        });
      }
    }
  }

  loadFromJSON() {
    const data = this.props.annotations.map(annotation => {
      let { type } = annotation.shape;

      if (type === "arrowLine") {
        type = "arrow";
      } else if (type === "i-text" || type === "InteractiveText") {
        type = "interactiveText";
      }
      // else if (type === "CustomPolygon") {
      //   type = "customPolygon";
      // }

      return {
        ...annotation.shape,
        type,
        _id: annotation._id,
        page: annotation.page
      };
    });

    this.canvas.loadFromJSON(
      { objects: data },
      () => {
        // move arrowheads to index of arrow body
        // otherwise they have different indices = render problems
        this.canvas.getObjects().forEach(item => {
          if (item.arrowId) {
            const annotation = filter(propEq("_id", item.arrowId), this.canvas.getObjects("arrow"))[0];
            const z = this.canvas.getObjects().indexOf(annotation);
            item.moveTo(z);
          }
        });
        this.renderAll();
      },
      (raw, instance) => {
        instance.set("page", raw.page);
        instance.set("_id", raw._id);
        this.addKeepITextInViewInterval(instance);
      }
    );
  }

  addKeepITextInViewInterval(shape) {
    if (shape.type !== "interactiveText") return;
    this.intervalReferences.push(
      setInterval(() => {
        if (shape.isEditing) {
          const scale = this.props.zoomController.getScale();
          const transform = this.props.zoomController.getTransform();
          const { params } = this.props.zoomController;

          const lineHeight = shape.__lineHeights[0];
          const cursorPosition = shape.get2DCursorLocation().lineIndex;

          let paddingTop = 0;
          if (params && params.padding && params.padding.top) {
            paddingTop = params.padding.top;
          }

          const cursorOffset = cursorPosition * lineHeight;
          const y = (-shape.top + lineHeight - cursorOffset) * scale + paddingTop;

          this.props.zoomController.moveTo(transform.x, y);
          window.scrollTo(0, 0);
          document.body.scrollTop = 0;
        }
      }, 1000)
    );
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { isDrawingMode, isFreeDrawing, primaryColor, toDelete, shapeType, strokeWidth } = this.props;
    const { isShiftPressed } = this.state;

    if (isShiftPressed !== prevState.isShiftPressed) {
      if (isShiftPressed) {
        this.enableSelection();
      } else {
        this.disableSelection();
      }
    }

    if (!equals(toDelete, prevProps.toDelete) && toDelete.length > 0) {
      const xs = innerJoin((record, _id) => record._id === _id, this.canvas.getObjects(), toDelete);

      this.canvas.remove(...xs);

      this.canvas.discardActiveObject();
      this.props.clearSelectionToDelete();
    }

    if (isDrawingMode !== prevProps.isDrawingMode || isFreeDrawing !== prevProps.isFreeDrawing) {
      this.canvas.isDrawingMode = isDrawingMode && isFreeDrawing;
    }
    if (includes(prevProps.shapeType, multiClickShapes) && prevProps.shapeType !== shapeType) {
      const { temporaryShape } = this.state;
      this.canvas.remove(temporaryShape);
      this.setState({
        temporaryShape: null
      });
    }

    this.updateCursor(isDrawingMode, isFreeDrawing);
    this.updateBrush(shapeType, primaryColor, strokeWidth);

    if (primaryColor !== prevProps.primaryColor) {
      this.canvas.getActiveObjects().forEach(obj => {
        if (!obj.fill || obj.fill.length === 0) {
          obj.set("stroke", primaryColor);
        } else {
          obj.set("fill", primaryColor);
        }

        if (obj.type === "arrow") {
          obj.arrow.set("fill", primaryColor);
        }

        this.triggerEvent("object:modified", {
          target: obj,
          transform: { action: "color" }
        });
      });

      this.renderAll();
    }

    if (strokeWidth !== prevProps.strokeWidth) {
      this.canvas.getActiveObjects().forEach(obj => {
        const { strokeLineCap, type, fill } = obj;
        /* eslint-disable prettier/prettier */
        if (
          (type === "path" && strokeLineCap === "round") || // free drawing
          (type === "rect" && fill === "") || // dashed rectangle
          (type === "ellipse" && fill === "") || // dashed circle
          type === "arrow" ||
          type === "line" ||
          (type === "customPolygon" && fill === "") // dashed triangle
        ) {
          /* eslint-enable prettier/prettier */
          obj.set("strokeWidth", strokeWidth);
          if (type === "arrow") {
            obj.arrow.set("width", ARROW_DEFAULT_DIMENSIONS + strokeWidth * 2);
            obj.arrow.set("height", ARROW_DEFAULT_DIMENSIONS + strokeWidth * 2);
          }
          this.triggerEvent("object:modified", {
            target: obj,
            transform: { action: "strokeWidth" }
          });
        }
      });
      this.renderAll();
    }
  }

  updateBrush(shapeType, primaryColor, strokeWidth) {
    switch (shapeType) {
      case MARKER:
        this.canvas.freeDrawingCursor = `url("${MarkerIcon}") 0 24, crosshair`;
        this.canvas.freeDrawingBrush.color = "rgba(255,250,0,0.4)";
        this.canvas.freeDrawingBrush.width = 18;
        this.canvas.freeDrawingBrush.strokeLineCap = "square";
        break;
      case FREE_DRAWING:
      default:
        this.canvas.freeDrawingCursor = `url("${FreeDrawingIcon}") 3 19, crosshair`;
        this.canvas.freeDrawingBrush.color = primaryColor;
        this.canvas.freeDrawingBrush.width = strokeWidth;
        this.canvas.freeDrawingBrush.strokeLineCap = "round";
        break;
    }
  }

  updateCursor(isDrawingMode, isFreeDrawing) {
    const icons = {
      FilledRectangle: `url("${RectangleFilledIcon}"), auto`,
      DashedRectangle: `url("${RectangleIcon}"), auto`,
      FilledCircle: `url("${EllipseIconFilled}"), auto`,
      DashedCircle: `url("${EllipseIcon}"), auto`,
      Line: `url("${LineIcon}"), auto`,
      Arrow: `url("${ArrowIcon}") 20 30, auto`,
      Text: `url("${TextIcon}"), auto`
    };

    let icon;
    if (!isDrawingMode || isFreeDrawing) {
      icon = "auto";
    } else {
      icon = icons[this.props.shapeType];
    }
    this.canvas.defaultCursor = icon;
  }

  renderAll() {
    this.canvas.renderAll();
  }

  componentWillUnmount() {
    this.canvas.off({
      "mouse:down": this.onMouseDown,
      "mouse:move": this.onMouseMove,
      "mouse:up": this.onMouseUp,
      "object:modified": this.onObjectModified,
      "path:created": this.onPathCreated,
      "selection:cleared": this.onSelectionCleared,
      "selection:updated": this.onSelectionUpdated,
      "selection:created": this.onSelectionCreated
    });

    EventBus.removeListener("fabricClearSelection", this.onClearSelection);

    EventBus.removeListener("issuedPrint", this.onIssuedPrint);
    EventBus.removeListener("finishedIssuingPrint", this.onFinishedIssuingPrint);

    document.removeEventListener("keydown", this.onKeyDown);
    document.removeEventListener("keyup", this.onKeyUp);
    document.removeEventListener("pointerup", this.onPointerUp);

    this.intervalReferences.forEach(ref => clearInterval(ref));

    this.clearSelectionForAll();
    this.canvas.dispose();
  }

  createMultiClickShape({ x, y }) {
    const _id = generateUUID();
    const options = {};
    options._id = _id;
    const { shapeType } = this.props;
    const points = [x, y];

    const shape = createShape(shapeType, { points, _id }, this.props.primaryColor, this.props.strokeWidth);

    this.addKeepITextInViewInterval(shape);
    this.canvas.add(shape);
    // this.canvas.setActiveObject(shape);
    this.setState({ temporaryShape: shape });
  }

  finalizeShape() {
    const { temporaryShape } = this.state;
    const { addAnnotation, page, bookId, setDrawingMode, setShapeType } = this.props;
    const { _id } = temporaryShape;

    temporaryShape.finalize?.();
    temporaryShape.toggleEdit?.();

    this.canvas.discardActiveObject();

    addAnnotation({
      _id,
      type: "annotation",
      page,
      shape: temporaryShape.toObject(),
      bookId
    });
    this.setState({ temporaryShape: null });
    // setDrawingMode(false);
    // setShapeType(null);
    this.renderAll();
  }

  additionalMultiClickShapeClick({ x, y }) {
    const { shapeType } = this.props;
    const { temporaryShape } = this.state;

    temporaryShape.onMouseMove({ x, y });
    switch (shapeType) {
      case LINE:
      case ARROW:
        // last point is being set in onMouseMove
        this.finalizeShape();
        break;
      case FILLED_TRIANGLE:
      case TRIANGLE:
        if (temporaryShape.hasMaxPoints()) {
          this.finalizeShape();
        } else temporaryShape.addPoint({ x, y });
        break;
      default:
        break;
    }
  }

  onMouseDown(event) {
    const { shapeType, zoomController, isDrawingMode, isFreeDrawing, isShiftPressed } = this.props;

    if ((isDrawingMode && isFreeDrawing) || isShiftPressed) {
      zoomController.pause();
    }
    if (event.target) {
      zoomController.pause();
    }

    if (includes(shapeType, multiClickShapes)) {
      const { temporaryShape } = this.state;
      const { x, y } = event.pointer;
      if (!temporaryShape) {
        if (this.canvas.getActiveObjects().length > 0) return;
        this.createMultiClickShape({ x, y });
      } else {
        this.additionalMultiClickShapeClick({ x, y });
      }
    }
    // else if(includes){}
  }

  onMouseUp(event) {
    this.props.zoomController.resume();

    const { shapeType, setShapeType, setDrawingMode } = this.props;
    if (this.props.isDrawingMode && !this.props.isFreeDrawing) {
      if (event.isClick) {
        if (includes(shapeType, oneTimeShapes)) {
          if (this.canvas.getActiveObjects().length > 0) return;
          const { x, y } = event.pointer;
          this.addShape(shapeType, { left: x, top: y });
          // setDrawingMode(false);
          // setShapeType(null);
        }
      }
    }
  }

  onMouseMove(event) {
    const { temporaryShape } = this.state;
    if (!temporaryShape) return;
    const { x, y } = event.pointer;
    temporaryShape.onMouseMove?.({ x, y });
    this.canvas.renderAll();
  }

  onPathCreated(event) {
    const { path } = event;
    const { addAnnotation, page, bookId } = this.props;

    const _id = generateUUID();
    path._id = _id;

    addAnnotation({
      _id,
      type: "annotation",
      page,
      shape: path.toObject(),
      bookId
    });
  }

  onObjectRotated(event) {
    const { target } = event;
    const SNAPPINESS_RANGE = 3;
    if (Math.min(target.angle, 360 - target.angle) < SNAPPINESS_RANGE) {
      target.rotate(0);
    }
    target.setCoords();
  }

  onObjectModified(event) {
    const { action } = event;
    switch (action) {
      case "rotate":
        this.onObjectRotated(event);
        break;
      case "scale":
      case "drag":
        this.onObjectMoved(event);
        break;
      default:
        break;
    }
  }

  onObjectMoved(event) {
    const { target } = event;

    target.setCoords();

    const targetCoords = Object.values(target.aCoords);

    const top = targetCoords.reduce((top, point) => Math.min(point.y, top), targetCoords[0].y);

    const bottom = targetCoords.reduce((bottom, point) => Math.max(point.y, bottom), targetCoords[0].y);

    const left = targetCoords.reduce((left, point) => Math.min(point.x, left), targetCoords[0].x);

    const right = targetCoords.reduce((right, point) => Math.max(point.x, right), targetCoords[0].x);

    const deltaTop = target.top - top;
    const deltaLeft = target.left - left;

    const height = bottom - top;
    const width = right - left;

    const boundaryMargin = this.state.cornerSize * 2;
    if (top + height < boundaryMargin) {
      target.top = -height + boundaryMargin + deltaTop;
    } else if (top > this.canvas.height - boundaryMargin) {
      target.top = this.canvas.height - boundaryMargin + deltaTop;
    }

    if (left + width < boundaryMargin) {
      target.left = -width + boundaryMargin + deltaLeft;
    } else if (left > this.canvas.width - boundaryMargin) {
      target.left = this.canvas.width - boundaryMargin + deltaLeft;
    }

    target.setCoords();
  }

  updateSelectionColor(shape) {
    let color = null;
    if (shape) {
      const { stroke, fill } = shape;
      if (stroke && stroke.length > 0) {
        color = stroke;
      } else {
        color = fill;
      }
    }
    this.props.setSelectionColorString(color);
  }

  updateStrokeWidth(shape) {
    if (shape) {
      const newStrokeWidth = DEFAULT_STROKE_WIDTH;
      const { strokeWidth, strokeLineCap, type, fill } = shape;
      /* eslint-disable prettier/prettier */
      // if (
      //   (type === "path" && strokeLineCap === "round") || // free drawing
      //   (type === "rect" && fill === "") || // dashed rectangle
      //   (type === "ellipse" && fill === "") || // dashed circle
      //   type === "arrow" ||
      //   type === "line"
      // ) {
      /* eslint-enable prettier/prettier */
      this.props.setSelectionStrokeWidth(strokeWidth || DEFAULT_STROKE_WIDTH);
      // } else {
      //   this.props.setSelectionStrokeWidth(DEFAULT_STROKE_WIDTH);

      // }
    } else {
      this.props.setSelectionStrokeWidth(null);
    }
  }

  onSelectionCreated(event) {
    const { selected } = event;
    this.props.addToSelection(selected);
    if (selected.length === 1 && selected[0].type === "polygon") {
      selected[0].toggleEdit();
    }
    const shape = this.canvas.getActiveObject();
    this.setState({ shape });
    this.updateSelectionColor(shape);
    this.updateStrokeWidth(shape);
  }

  onSelectionCleared(event) {
    const { temporaryShape } = this.state;
    if (temporaryShape) return;
    this.props.clearSelection();
    this.updateDeselectedAnnotations(event.deselected);
    this.setState({ shape: null });
    this.updateSelectionColor(null);
    this.updateStrokeWidth(null);
  }

  onSelectionUpdated(event) {
    const { temporaryShape } = this.state;
    if (temporaryShape) return;
    const { selected, deselected } = event;
    this.props.removeFromSelection(deselected);
    this.props.addToSelection(selected);
    this.updateDeselectedAnnotations(deselected);
    this.setState({ shape: this.canvas.getActiveObject() });
    this.updateSelectionColor(this.canvas.getActiveObjects()[0]);
    this.updateStrokeWidth(this.canvas.getActiveObjects()[0]);
  }

  updateDeselectedAnnotations(annotations) {
    if (!annotations) return;
    for (let deselected of annotations) {
      deselected = this.getAnnotation(deselected);

      if (deselected.type === "arrow") {
        deselected.arrow.fill = deselected.stroke;
      }

      this.props.updateAnnotation({
        _id: deselected._id,
        shape: deselected.toObject()
      });
    }
  }

  getAnnotation(annotationRef = null) {
    let annotation = annotationRef || this.canvas.getActiveObject();
    if (!annotation) return null;
    if (annotation.arrowHead || annotation.arrowTail) {
      annotation = filter(propEq("_id", annotation.arrowId), this.canvas.getObjects("arrow"))[0];
    }
    return annotation;
  }

  addShape(shapeType, options) {
    const { addAnnotation, page, bookId } = this.props;

    const _id = generateUUID();
    options._id = _id;

    const shape = createShape(shapeType, options, this.props.primaryColor, this.props.strokeWidth);
    this.addKeepITextInViewInterval(shape);

    this.canvas.add(shape);
    if (shapeType === TEXT) {
      this.canvas.setActiveObject(shape);
    }

    addAnnotation({
      _id,
      type: "annotation",
      page,
      shape: shape.toObject(),
      bookId
    });
  }

  triggerEvent(name, event) {
    this.canvas.fire(name, event);
  }

  enableSelection() {
    this.canvas.selection = true;
  }

  disableSelection() {
    this.canvas.selection = false;
  }

  onKeyDown(event) {
    if (event.keyCode === 16 || event.key === "Key") {
      this.setState({ isShiftPressed: true });
    }
  }

  onKeyUp(event) {
    if (event.keyCode === 16 || event.key === "Key") {
      this.setState({ isShiftPressed: false });
    } else if (event.keyCode === 46 || event.key === "Delete") {
      this.props.deleteSelected();
    }
  }

  /**
   * Clear selection in redux store and notify
   * all fabric instances to discard their current selection.
   *
   * Save selected objects in this canvas instance.
   */
  clearSelectionForAll() {
    this.props.clearSelection();
    EventBus.dispatch("fabricClearSelection");

    if (this.canvas) {
      this.updateDeselectedAnnotations(this.canvas.getActiveObjects());
    }
  }

  onClickBold() {
    const { shape } = this.state;
    const textLength = shape.text.length;

    let { selectionStart } = shape;
    let { selectionEnd } = shape;
    let fontWeight = "";

    const fullText = shape.getSelectionStyles(0, textLength);
    const fullSelection = shape.getSelectionStyles(selectionStart, selectionEnd);
    const boldText = getBoldSelection(fullText);
    const boldSelection = getBoldSelection(fullSelection);

    const isEmptySelection = fullSelection.length === 0;
    const isFullBoldText = fullText.length === boldText.length;
    const isFullBoldSelection = fullSelection.length === boldSelection.length;

    if (isEmptySelection) {
      fontWeight = isFullBoldText ? "normal" : "bold";
      selectionStart = 0;
      selectionEnd = textLength;
    } else {
      fontWeight = isFullBoldSelection ? "normal" : "bold";
      selectionStart = shape.selectionStart;
      selectionEnd = shape.selectionEnd;
    }

    shape.setSelectionStyles({ fontWeight }, selectionStart, selectionEnd);
    this.canvas.renderAll();
  }

  onClickItalic() {
    const { shape } = this.state;
    const textLength = shape.text.length;

    let { selectionStart } = shape;
    let { selectionEnd } = shape;
    let fontStyle = "";

    const fullText = shape.getSelectionStyles(0, textLength);
    const fullSelection = shape.getSelectionStyles(selectionStart, selectionEnd);
    const italicText = getItalicSelection(fullText);
    const italicSelection = getItalicSelection(fullSelection);

    const isEmptySelection = fullSelection.length === 0;
    const isFullItalicText = fullText.length === italicText.length;
    const isFullItalicSelection = fullSelection.length === italicSelection.length;

    if (isEmptySelection) {
      fontStyle = isFullItalicText ? "normal" : "italic";
      selectionStart = 0;
      selectionEnd = textLength;
    } else {
      fontStyle = isFullItalicSelection ? "normal" : "italic";
      selectionStart = shape.selectionStart;
      selectionEnd = shape.selectionEnd;
    }

    shape.setSelectionStyles({ fontStyle }, selectionStart, selectionEnd);
    this.canvas.renderAll();
  }

  onClickUnderline() {
    const { shape } = this.state;
    const textLength = shape.text.length;

    let { selectionStart } = shape;
    let { selectionEnd } = shape;
    let underline = true;

    const fullText = shape.getSelectionStyles(0, textLength);
    const fullSelection = shape.getSelectionStyles(selectionStart, selectionEnd);
    const underlinedText = getUnderlineSelection(fullText);
    const underlinedSelection = getUnderlineSelection(fullSelection);

    const isEmptySelection = fullSelection.length === 0;
    const isFullUnderlinedText = fullText.length === underlinedText.length;
    const isFullUnderlinedSelection = fullSelection.length === underlinedSelection.length;

    if (isEmptySelection) {
      underline = !isFullUnderlinedText;
      selectionStart = 0;
      selectionEnd = textLength;
    } else {
      underline = !isFullUnderlinedSelection;
      selectionStart = shape.selectionStart;
      selectionEnd = shape.selectionEnd;
    }

    shape.setSelectionStyles({ underline }, selectionStart, selectionEnd);
    this.canvas.renderAll();
  }

  render() {
    const { shape } = this.state;
    const { viewerOrigin, page } = this.props;
    let dataViewerOrigin = "";

    if (viewerOrigin === "main") {
      dataViewerOrigin = `${viewerOrigin}_${page}`;
    } else {
      dataViewerOrigin = viewerOrigin;
    }

    return (
      <Cloth ref={this.wrapperRef}>
        <Menu>
          {shape && shape.type === "interactiveText" && (
            <InlineMenu shape={shape}>
              <IconButton onClick={this.onClickBold} className="panzoomAllowTouch" size="large" aria-label="Fett">
                <BoldIcon />
              </IconButton>
              <IconButton onClick={this.onClickItalic} className="panzoomAllowTouch" size="large" aria-label="Kursiv">
                <ItalicIcon />
              </IconButton>
              <IconButton
                onClick={this.onClickUnderline}
                className="panzoomAllowTouch"
                size="large"
                aria-label="Unterstrichen"
              >
                <UnderlinedIcon />
              </IconButton>
            </InlineMenu>
          )}
        </Menu>
        <Canvas data-viewerorigin={dataViewerOrigin} ref={this.canvasRef} />
      </Cloth>
    );
  }
}

LinenCloth.defaultProps = {
  canvasSettings: {
    selection: false,
    selectionColor: "rgba(255, 0, 0, 0.3)",
    selectionBorderColor: "rgb(255, 0, 0)",
    selectionLineWidth: 2,
    selectionKey: "shiftKey"
  },
  objectSettings: {
    cornerStyle: "circle",
    transparentCorners: false,
    borderColor: "#00bcd4",
    cornerColor: "#00bcd4",
    cornerSize: 24,
    borderScaleFactor: 2,
    padding: 4
  },
  rotatingPointOffset: -48 // deprecated so needs to be added differently
};

LinenCloth.propTypes = {
  bookId: PropTypes.string.isRequired,
  page: PropTypes.number.isRequired,
  zoomController: PropTypes.object.isRequired,
  classes: PropTypes.object,
  canvasSettings: PropTypes.object,
  objectSettings: PropTypes.object,
  isDrawingMode: PropTypes.bool.isRequired,
  isFreeDrawing: PropTypes.bool.isRequired,
  shapeType: PropTypes.string,
  primaryColor: PropTypes.string,
  annotations: PropTypes.array,
  toDelete: PropTypes.array,
  addAnnotation: PropTypes.func.isRequired,
  updateAnnotation: PropTypes.func.isRequired,
  setShapeType: PropTypes.func.isRequired,
  addToSelection: PropTypes.func.isRequired,
  removeFromSelection: PropTypes.func.isRequired,
  clearSelectionToDelete: PropTypes.func.isRequired,
  clearSelection: PropTypes.func.isRequired,
  setDrawingMode: PropTypes.func.isRequired,
  deleteSelected: PropTypes.func.isRequired,
  setSelectionColorString: PropTypes.func.isRequired,
  pageWidth: PropTypes.number,
  pageHeight: PropTypes.number,
  viewerOrigin: PropTypes.string.isRequired,
  strokeWidth: PropTypes.number.isRequired,
  setSelectionStrokeWidth: PropTypes.func.isRequired,
  rotatingPointOffset: PropTypes.number.isRequired
};

export default LinenCloth;
